<template>
  <div
    id="SlideUnlock"
    class="slide-unlock"
    :class="{ unlocked: isUnlocked }"
    :style="{ maxWidth }"
  >
    <span class="slide-unlock__label" aria-hidden="true">
      {{ isUnlocked ? unlockedText : label }}
    </span>
    <div
      ref="thumbRef"
      class="slide-unlock__thumb"
      :class="{ dragging: isDragging }"
      :style="{ transform: `translateX(${currentX}px)` }"
      role="slider"
      :aria-valuenow="progressPercent"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-label="label"
      @mousedown.prevent="onStart"
      @touchstart.prevent="onStart"
    >
      <span class="slide-unlock__thumb-icon" aria-hidden="true">
        {{ isUnlocked ? unlockedIcon : icon }}
      </span>
    </div>
  </div>
</template>

<script setup>
/**
 * スライドしてロックを解除するUIコンポーネント
 *
 * サムをトラック末端までドラッグすることで unlock イベントを emit する
 * ドラッグが途中で終わった場合は元の位置に戻る
 */
import { ref, computed, onBeforeUnmount } from 'vue'

const props = defineProps({
  /**
   * スライド前に表示するラベル文言
   */
  label: {
    type: String,
    default: 'スライドして下さい'
  },
  /**
   * アンロック完了後に表示するラベル文言
   */
  unlockedText: {
    type: String,
    default: '確認済み'
  },
  /**
   * スライド前に表示するサムアイコン文字
   */
  icon: {
    type: String,
    default: '›'
  },
  /**
   * アンロック完了後に表示するサムアイコン文字
   */
  unlockedIcon: {
    type: String,
    default: '✓'
  },
  /**
   * アンロックと判定するしきい値（0〜1）
   * サムがトラック末端からこの割合以内に到達したらアンロックとみなす
   */
  threshold: {
    type: Number,
    default: 0.9
  },
  /**
   * ドラッグ時間がこの値（ms）未満の場合は解除をキャンセルする
   */
  minDragDurationMs: {
    type: Number,
    default: 300
  },
  /**
   * moveイベント回数がこの値未満の場合は解除をキャンセルする
   */
  minMoveEvents: {
    type: Number,
    default: 8
  },
  /**
   * マウント後、この秒数未満の解除操作はキャンセルする（0で無効）
   */
  minUnlockSeconds: {
    type: Number,
    default: 1
  },
  /**
   * スライドUIの最大幅
   * CSSの値をそのまま受け取る
   */
  maxWidth: {
    type: String,
    default: '300px'
  }
})

const emit = defineEmits(['unlock'])

/** スライド完了状態 */
const isUnlocked = ref(false)

/** ドラッグ中フラグ */
const isDragging = ref(false)

/** サムの現在のX座標（px） */
const currentX = ref(0)

/** ドラッグ開始時のポインターX座標 */
let startPointerX = 0

/** ドラッグ開始時のサムX座標 */
let startThumbX = 0

/** トラックの最大移動幅（px）。ドラッグ開始時に算出する */
let maxX = 0

/** ドラッグ開始時刻（ms） */
let dragStartedAtMs = 0

/** ドラッグ中に発火したmoveイベント回数 */
let moveEventCount = 0

/** サムのDOM参照 */
const thumbRef = ref(null)

/** コンポーネントのマウント時刻（ms） */
const mountedAtMs = Date.now()

/**
 * 進捗率（0〜100）
 */
const progressPercent = computed(() =>
  maxX > 0 ? Math.round((currentX.value / maxX) * 100) : 0
)

/**
 * ドラッグ開始処理
 * @param {MouseEvent|TouchEvent} event
 */
function onStart(event) {
  if (isUnlocked.value) return

  isDragging.value = true
  startPointerX = getClientX(event)
  startThumbX = currentX.value
  dragStartedAtMs = Date.now()
  moveEventCount = 0

  const track = thumbRef.value?.parentElement
  if (track && thumbRef.value) {
    // 左右の余白を統一するため、初期オフセット分を両端に確保する
    const horizontalOffset = thumbRef.value.offsetLeft
    maxX = Math.max(
      track.clientWidth - thumbRef.value.clientWidth - horizontalOffset * 2,
      0
    )
  }

  window.addEventListener('mousemove', onMove)
  window.addEventListener('mouseup', onEnd)
  window.addEventListener('touchmove', onMove, { passive: false })
  window.addEventListener('touchend', onEnd)
  window.addEventListener('touchcancel', onEnd)
}

/**
 * ドラッグ移動処理
 * @param {MouseEvent|TouchEvent} event
 */
function onMove(event) {
  if (!isDragging.value) return
  event.preventDefault()

  const delta = getClientX(event) - startPointerX
  const next = Math.min(Math.max(startThumbX + delta, 0), maxX)
  moveEventCount += 1
  currentX.value = next
}

/**
 * ドラッグ終了処理
 */
function onEnd() {
  if (!isDragging.value) return
  isDragging.value = false
  removeListeners()

  // マウント直後の解除は受け付けない
  if (Date.now() - mountedAtMs < props.minUnlockSeconds * 1000) {
    currentX.value = 0
    return
  }

  // 指を離した時点でしきい値以上の場合のみ解除する
  if (maxX > 0 && currentX.value / maxX >= props.threshold) {
    currentX.value = maxX
    isUnlocked.value = true
    emit('unlock')
    return
  } 

  // ドラッグ時間が短すぎる操作はアンロック対象にしない
  const dragDurationMs = Date.now() - dragStartedAtMs
  if (dragDurationMs < props.minDragDurationMs) {
    currentX.value = 0
    return
  }

  // moveイベント回数が少なすぎる操作はアンロック対象にしない
  if (moveEventCount < props.minMoveEvents) {
    currentX.value = 0
    return
  }

  if (maxX > 0 && currentX.value / maxX >= props.threshold) {
    currentX.value = maxX
    isUnlocked.value = true
    emit('unlock')
  } else {
    currentX.value = 0
  }
}

/**
 * イベントリスナーを削除する
 */
function removeListeners() {
  window.removeEventListener('mousemove', onMove)
  window.removeEventListener('mouseup', onEnd)
  window.removeEventListener('touchmove', onMove)
  window.removeEventListener('touchend', onEnd)
  window.removeEventListener('touchcancel', onEnd)
}

/**
 * ポインターのクライアントX座標を取得する
 * @param {MouseEvent|TouchEvent} event
 * @returns {number}
 */
function getClientX(event) {
  return event.touches ? event.touches[0].clientX : event.clientX
}

/**
 * アンロック状態をリセットする（外部から呼び出し可能）
 */
function reset() {
  isUnlocked.value = false
  currentX.value = 0
}

defineExpose({ reset })

onBeforeUnmount(() => {
  removeListeners()
})
</script>

<style lang="scss" scoped>
.slide-unlock {
  position: relative;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  height: 48px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 24px;
  overflow: hidden;
  user-select: none;
  touch-action: none;

  &__label {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    color: #666;
    pointer-events: none;
    transition: opacity 0.2s;
  }

  &__thumb {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 40px;
    height: 40px;
    background-color: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    transition:
      transform 0.2s ease,
      background-color 0.2s;
    will-change: transform;

    &.dragging {
      cursor: grabbing;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
      transition: background-color 0.2s;
    }
  }

  &__thumb-icon {
    font-size: 1.25rem;
    color: #555;
    line-height: 1;
    pointer-events: none;
  }

  &.unlocked {
    background-color: #4caf50;

    .slide-unlock__label {
      color: #fff;
    }

    .slide-unlock__thumb {
      background-color: #fff;
      cursor: default;
    }

    .slide-unlock__thumb-icon {
      color: #4caf50;
    }
  }
}
</style>
